import { useState }   from "react";
import { Target, Flag, ChevronRight, Star, AlertCircle, Zap } from "lucide-react";
import { ROADMAP_PHASES, ROADMAP_KEY_TAKEAWAYS } from "../../data/goalsData";

/**
 * NinetyDayRoadmap
 * Expandable phase-by-phase 90-day plan shown inside GoalsTab.
 * Content is driven entirely by ROADMAP_PHASES and ROADMAP_KEY_TAKEAWAYS
 * in goalsData.js — no changes needed here to update copy.
 */
export default function NinetyDayRoadmap() {
  const [openPhase, setOpenPhase] = useState(0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header banner */}
      <div className="card" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)", border: "none" }}>
        <div style={{ padding: "24px 24px 20px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <span style={{ fontSize: 32 }}>🗺️</span>
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: "#fff", margin: "0 0 6px" }}>
                Your 90-Day Roadmap to Crushing It
              </h3>
              <p style={{ fontSize: 13, color: "#bfdbfe", margin: 0, lineHeight: 1.6 }}>
                Every top-performing rep at Propertunity Capital followed a version of this plan.
                It's not complicated — but it IS specific. Follow it, and you'll be closing deals by month three.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Phase cards */}
      {ROADMAP_PHASES.map((phase, idx) => {
        const isOpen = openPhase === idx;
        return (
          <div key={idx} className="card" style={{ borderColor: phase.borderColor }}>
            <div
              className="card-header"
              style={{ background: phase.lightColor, borderColor: phase.borderColor, cursor: "pointer" }}
              onClick={() => setOpenPhase(isOpen ? -1 : idx)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 22 }}>{phase.icon}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: phase.color, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                    {phase.phase}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{phase.headline}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{
                  fontSize: 11, color: phase.color, background: "#fff",
                  border: `1px solid ${phase.borderColor}`,
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "3px 8px", borderRadius: 20, fontWeight: 600,
                }}>
                  <Flag size={10} />{phase.goalLabel}
                </span>
                <ChevronRight
                  size={15} color={phase.color}
                  style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                />
              </div>
            </div>

            {isOpen && (
              <div className="card-body">

                {/* Phase goal */}
                <div style={{
                  background: phase.lightColor, border: `1px solid ${phase.borderColor}`,
                  borderRadius: 8, padding: "12px 14px", marginBottom: 18,
                  display: "flex", gap: 10, alignItems: "flex-start",
                }}>
                  <Target size={15} color={phase.color} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: phase.color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>
                      Phase Goal
                    </div>
                    <div style={{ fontSize: 13.5, color: "#1e293b", fontWeight: 500 }}>{phase.goal}</div>
                  </div>
                </div>

                {/* Action steps */}
                <p style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                  Action Steps
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 18 }}>
                  {phase.steps.map((step, si) => (
                    <div key={si} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 12px", background: "#f8fafc", borderRadius: 7, border: "1px solid #f1f5f9" }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: "50%", flexShrink: 0, marginTop: 1,
                        background: phase.color, color: "#fff", fontSize: 11, fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {si + 1}
                      </div>
                      <span style={{ fontSize: 13.5, color: "#334155", lineHeight: 1.55 }}>{step}</span>
                    </div>
                  ))}
                </div>

                {/* Callout */}
                <div style={{
                  background: phase.callout.type === "tip" ? "#fffbeb" : "#fef2f2",
                  border: `1px solid ${phase.callout.type === "tip" ? "#fde68a" : "#fecaca"}`,
                  borderRadius: 8, padding: "12px 14px",
                  display: "flex", gap: 10, alignItems: "flex-start",
                }}>
                  {phase.callout.type === "tip"
                    ? <Star      size={15} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
                    : <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                  }
                  <div>
                    <div style={{
                      fontSize: 12, fontWeight: 700,
                      color: phase.callout.type === "tip" ? "#92400e" : "#991b1b",
                      marginBottom: 3,
                    }}>
                      {phase.callout.label}
                    </div>
                    <div style={{
                      fontSize: 13,
                      color: phase.callout.type === "tip" ? "#92400e" : "#991b1b",
                      lineHeight: 1.6,
                    }}>
                      {phase.callout.text}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Key Takeaways */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <Zap size={15} style={{ display: "inline", marginRight: 6, color: "#d97706" }} />
            Key Takeaways
          </span>
        </div>
        <div className="card-body">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {ROADMAP_KEY_TAKEAWAYS.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "9px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #f1f5f9" }}>
                <span style={{ fontSize: 17, flexShrink: 0 }}>{t.icon}</span>
                <span style={{ fontSize: 13.5, color: "#334155", lineHeight: 1.55 }}>{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}